import { Clock, XCircle, X } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";

/**
 * Reusable Notification Card Component
 * 
 * Props:
 * @param {string} variant - "warning" (amber) | "error" (red) | "info" (blue)
 * @param {React.ReactNode} icon - Icon component to display
 * @param {string} title - Notification title
 * @param {React.ReactNode} children - Content/body of the notification
 * @param {Function} onDismissSession - Handler for "Dismiss Until Login" button
 * @param {Function} onDismissPermanent - Handler for permanent dismiss (optional)
 * @param {string} permanentDismissLabel - Label for permanent dismiss button (default: "Dismiss for Today")
 * @param {React.ReactNode} actions - Additional action buttons (optional)
 * @param {string} footer - Footer text (optional)
 * @param {string} testId - data-testid for testing
 */
export const NotificationCard = ({
  variant = "warning",
  icon,
  title,
  children,
  onDismissSession,
  onDismissPermanent,
  permanentDismissLabel = "Dismiss for Today",
  actions,
  footer,
  testId,
}) => {
  const variants = {
    warning: {
      card: "border-amber-300 bg-amber-50 dark:bg-amber-950/20",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      title: "text-amber-800 dark:text-amber-200",
      sessionBtn: "text-amber-700 border-amber-300 hover:bg-amber-100",
      footer: "text-amber-600",
    },
    error: {
      card: "border-red-300 bg-red-50 dark:bg-red-950/20",
      iconBg: "bg-red-100",
      iconColor: "text-red-600",
      title: "text-red-800 dark:text-red-200",
      sessionBtn: "text-red-700 border-red-300 hover:bg-red-100",
      footer: "text-red-600",
    },
    info: {
      card: "border-blue-300 bg-blue-50 dark:bg-blue-950/20",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      title: "text-blue-800 dark:text-blue-200",
      sessionBtn: "text-blue-700 border-blue-300 hover:bg-blue-100",
      footer: "text-blue-600",
    },
  };

  const styles = variants[variant] || variants.warning;

  return (
    <Card className={styles.card} data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`w-10 h-10 ${styles.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
            <span className={styles.iconColor}>{icon}</span>
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Header with title and dismiss button */}
            <div className="flex items-center justify-between gap-2 mb-2">
              <h3 className={`font-semibold ${styles.title}`}>
                {title}
              </h3>
              {onDismissSession && (
                <Button
                  variant="outline"
                  size="sm"
                  className={`${styles.sessionBtn} flex items-center gap-1`}
                  onClick={onDismissSession}
                  data-testid={`${testId}-dismiss-session`}
                >
                  <Clock className="w-3 h-3" />
                  Dismiss Until Login
                </Button>
              )}
            </div>
            
            {/* Content */}
            <div className="mb-3">
              {children}
            </div>

            {/* Actions Row */}
            {(actions || onDismissPermanent) && (
              <div className="flex flex-wrap items-center gap-2">
                {actions}
                {onDismissPermanent && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-600 hover:text-gray-800"
                    onClick={onDismissPermanent}
                    data-testid={`${testId}-dismiss-permanent`}
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    {permanentDismissLabel}
                  </Button>
                )}
              </div>
            )}

            {/* Footer */}
            {footer && (
              <p className={`text-xs ${styles.footer} mt-2`}>
                {footer}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Notification Item Row Component
 * For notifications with multiple dismissible items (like jobs)
 * 
 * Props:
 * @param {React.ReactNode} children - Item content (usually a link)
 * @param {Function} onDismiss - Handler for dismissing this specific item
 * @param {string} dismissLabel - Label for dismiss button
 * @param {string} dismissTitle - Tooltip for dismiss button
 * @param {string} testId - data-testid for testing
 */
export const NotificationItem = ({
  children,
  onDismiss,
  dismissLabel = "No Work Today",
  dismissTitle = "Dismiss this item permanently for today",
  testId,
}) => {
  return (
    <div className="flex items-center justify-between bg-white/50 dark:bg-gray-800/50 rounded px-2 py-1.5">
      <div className="flex-1">{children}</div>
      {onDismiss && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-gray-600 hover:text-red-600 hover:bg-red-50"
          onClick={onDismiss}
          title={dismissTitle}
          data-testid={testId}
        >
          <X className="w-3.5 h-3.5 mr-1" />
          {dismissLabel}
        </Button>
      )}
    </div>
  );
};
